import { Controller } from "@hotwired/stimulus";
import PinchZoom from "pinch-zoom-js";
import { WebShareNavigator } from "shared/WebShareNavigator";

export default class extends Controller {
  static targets = [
    "imageModal",
    "imageModalContent",
    "imageDownload",
    "imageModalClose",
    "pdfModal",
    "fileName",
    "pdfLink",
    "tokenForm",
  ];

  /**
   * モーダルを表示する
   *   image: 画像プレビューモーダル
   *   pdf: PDFを開く確認用モーダル
   */
  showModal(event) {
    const fileType = event.currentTarget.dataset.fileType;
    const fileName = event.currentTarget.dataset.filename;
    const url = event.currentTarget.dataset.url;

    if (fileType === "pdf") {
      this.fileNameTarget.textContent = fileName;

      const fileId = event.currentTarget.dataset.fileId;
      this.#getTokenAfterShowModal(fileId);
    } else {
      // モーダルの閉じるボタンをロックして画像読み込みをする
      // 低速ネットワークでモーダルの開閉を繰り返すと要素が重複して生成される為
      this.imageModalCloseTarget.disabled = true;
      this.#setImage(url);
      this.imageModalTarget.classList.remove("d-none");

      // ダウンロードボタンが表示されている場合はダウンロードURLを更新する
      if (this.hasImageDownloadTarget) {
        this.imageDownloadTarget.dataset.url = url;
        this.imageDownloadTarget.dataset.fileType = fileType;
        this.imageDownloadTarget.dataset.filename = fileName;
      }
    }
  }

  /**
   * 画像のモーダルを閉じる
   */
  closeImageModal() {
    this.imageModalTarget.classList.add("d-none");

    // 生成したピンチズームを削除
    // modal-bodyの親要素として生成されるので、modal-bodyごと削除される
    if (this.pinchZoomInstance) {
      this.pinchZoomInstance.destroy();
    }
  }

  /**
   * ファイルをダウンロードする
   * MEMO: 今のところはスタッフアプリで画像のみダウンロードできる
   */
  async download(event) {
    const fileType = event.currentTarget.dataset.fileType;
    const fileName = event.currentTarget.dataset.filename;
    const url = event.currentTarget.dataset.url;

    const response = await fetch(url);
    const blob = await response.blob();
    const files = [new File([blob], fileName, { type: fileType })];

    const shareData = {
      files: files,
      title: fileName,
    };

    if (WebShareNavigator.canShare(shareData)) {
      try {
        WebShareNavigator.share(shareData);
      } catch (error) {
        console.warn(error);
        throw error;
      }
    } else {
      alert("共有機能に対応していません。");
    }
  }

  #getTokenAfterShowModal(fileId) {
    // トークン発行を行う form を特定する
    const form = this.tokenFormTargets.find(
      (target) => target.dataset.fileId === fileId,
    );

    // ファイルアクセスに必要なトークン発行
    fetch(form.action, {
      method: "GET",
    })
      .then((response) => {
        if (!response.ok) {
          console.error(`トークン取得に失敗 (${response.status})`);
        }
        return response.json();
      })
      .then((data) => {
        // トークンを付けたURLにする
        this.pdfLinkTarget.href = `${data.url}`;
        window.BootstrapWrapper.showModal(this.pdfModalTarget);
      })
      .catch((error) => {
        console.error("エラーが発生しました: ", error);
        alert("ファイルの読み込みに失敗しました。再試行してください。");
      });
  }

  /**
   * モーダルに表示する画像をセットする
   */
  #setImage(url) {
    // modal-bodyを作成して、フッターの上に挿入する
    this.imageModalBody = document.createElement("div");
    this.imageModalBody.classList.add("talkroom-img-modal-body");
    const footerElement = this.imageModalContentTarget.querySelector(
      ".talkroom-img-modal-footer",
    );
    this.imageModalContentTarget.insertBefore(
      this.imageModalBody,
      footerElement,
    );

    // モーダルに表示する画像を生成する
    this.imagePreviewImg = document.createElement("img");
    this.imageModalBody.appendChild(this.imagePreviewImg);

    // 画像の読み込み完了後にピンチズームの初期化をする
    this.imagePreviewImg.onload = this.#initializePinchZoom.bind(this);

    // 画像読み込み失敗時はモーダルの閉じるを有効化する
    this.imagePreviewImg.onerror = this.#onErrorImageModal.bind(this);

    this.imagePreviewImg.src = url;
  }

  /**
   * ピンチズームの動作を初期化する
   */
  #initializePinchZoom() {
    if (this.imageModalBody) {
      this.pinchZoomInstance = new PinchZoom(this.imageModalBody, {});
    }

    // モーダルの閉じる動作を有効化する
    this.imageModalCloseTarget.disabled = false;
  }

  /**
   * 画像の読み込み失敗時の処理
   */
  #onErrorImageModal() {
    // モーダルの閉じる動作を有効化する
    this.imageModalCloseTarget.disabled = false;

    // modal-bodyを削除する
    if (this.imageModalBody) {
      this.imageModalContentTarget.removeChild(this.imageModalBody);
    }

    alert("画像の読み込みに失敗しました。");
  }
}
